import React, { useState, useMemo } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, ButtonBase } from '@mui/material';
import {
  ResponsiveContainer, ComposedChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { MonthlyDynamicsRow } from '../../types';
import { fmtSum, fmtSumShort } from '../../utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARDS_PAST = 3;     // сколько прошедших месяцев показываем по умолчанию (expiry-режим)
const CARDS_FUTURE = 9;   // сколько будущих месяцев показываем по умолчанию (expiry-режим)
const CARDS_VISIBLE = CARDS_PAST + CARDS_FUTURE; // 12 — дефолтное окно

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Возвращает строку вида "YYYY-MM" для текущего месяца и N месяцев вперёд.
 */
function getUpcomingRange(months: number): { from: string; to: string } {
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const future = new Date(now.getFullYear(), now.getMonth() + months, 1);
  const to = `${future.getFullYear()}-${String(future.getMonth() + 1).padStart(2, '0')}`;
  return { from, to };
}

function isUpcomingExpiry(month: string, from: string, to: string): boolean {
  return month >= from && month <= to;
}

/**
 * Умный интервал подписей оси X — предотвращает наложение при большом диапазоне.
 */
function calcTickInterval(count: number): number {
  if (count <= 18) return 0;          // показываем все
  if (count <= 36) return 2;          // каждый 3-й
  if (count <= 60) return 4;          // каждый 5-й
  return Math.ceil(count / 12);       // ~12 подписей максимум
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

interface DynamicsTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  mainColor: string;
  isExpiry: boolean;
}

const DynamicsTooltip: React.FC<DynamicsTooltipProps> = ({
  active, payload, label, mainColor, isExpiry,
}) => {
  if (!active || !payload?.length) return null;

  // Дедупликация: Area + Line оба имеют dataKey="total", фильтруем по первому вхождению
  const seen = new Set<string>();
  const unique = payload.filter((p: any) => {
    if (seen.has(p.dataKey)) return false;
    seen.add(p.dataKey);
    return true;
  });
  const entry = unique[0];
  if (!entry) return null;

  const row: MonthlyDynamicsRow = entry.payload;
  const monthStr = label || row?.month;
  const formattedMonth = monthStr
    ? new Date(monthStr + '-01').toLocaleString('ru', { month: 'short', year: '2-digit' })
    : '';

  return (
    <Box sx={{
      bgcolor: '#1e293b', color: 'white',
      px: 2, py: 1.5, borderRadius: 2,
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)', minWidth: 190,
      pointerEvents: 'none',
    }}>
      <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mb: 0.75 }}>
        {formattedMonth}{isExpiry ? ' · срок годности' : ' · дата записи'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: mainColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800 }}>
          {fmtSum(entry.value)}
        </Typography>
      </Box>
      {(row?.totalQty != null || row?.count != null) && (
        <Box sx={{ mt: 0.25, pl: 2.5 }}>
          {row.totalQty != null && (
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {row.totalQty.toLocaleString('ru-RU')} уп.
            </Typography>
          )}
          {row.count != null && (
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {row.count.toLocaleString('ru-RU')} позиций
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface MonthlyDynamicsChartProps {
  data: MonthlyDynamicsRow[];
  expiryData?: MonthlyDynamicsRow[];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * График 1: Помесячная динамика суммы (ComposedChart = Area + Line).
 * Поддерживает:
 * - переключение режимов: дата записи / срок годности
 * - анимацию отрисовки линии слева направо (isAnimationActive + animationDuration)
 * - точное сопоставление точек при наведении (XAxis dataKey="month")
 * - умный интервал и наклон подписей оси X при большом диапазоне данных
 * - лимит карточек внизу (3 прошедших + 9 будущих) с кнопкой «Показать всё»
 * - подсветку истекающих месяцев (следующие 3 мес.) в режиме ExpiryDate
 */
const MonthlyDynamicsChart: React.FC<MonthlyDynamicsChartProps> = ({ data, expiryData = [] }) => {
  const [mode, setMode] = useState<'recordDate' | 'expiryDate'>('recordDate');
  const [showAllCards, setShowAllCards] = useState(false);

  const isExpiry = mode === 'expiryDate';
  const mainColor = isExpiry ? '#f59e0b' : '#6366f1';
  const activeColor = isExpiry ? '#d97706' : '#4f46e5';

  const currentData = mode === 'recordDate' ? data : expiryData;

  // Диапазон «истекает скоро» — текущий + 3 следующих месяца
  const upcomingRange = useMemo(() => getUpcomingRange(3), []);

  const chartData = useMemo(() =>
    currentData.map(d => ({
      ...d,
      label: new Date(d.month + '-01').toLocaleString('ru', { month: 'short', year: '2-digit' }),
    })),
    [currentData]
  );

  // Умный интервал подписей оси X
  const tickInterval = calcTickInterval(chartData.length);
  const rotateLabels = chartData.length > 24;
  const xAxisHeight = rotateLabels ? 52 : 30;
  const xAxisAngle = rotateLabels ? -45 : 0;
  const xAxisAnchor = rotateLabels ? 'end' : 'middle';

  /**
   * Вычисляем окно карточек:
   * - expiry-режим: 3 прошедших + 9 будущих, центр = сегодня
   * - record-режим: последние 12 (все записи в прошлом)
   */
  const { cardStart, cardEnd } = useMemo(() => {
    if (!isExpiry) {
      // record-режим — просто последние CARDS_VISIBLE
      const end = currentData.length;
      const start = Math.max(0, end - CARDS_VISIBLE);
      return { cardStart: start, cardEnd: end };
    }
    // expiry-режим: pivot = первый месяц >= сегодня
    const pivotIdx = currentData.findIndex(d => d.month >= upcomingRange.from);
    const pivot = pivotIdx === -1 ? currentData.length : pivotIdx;
    const start = Math.max(0, pivot - CARDS_PAST);
    const end = Math.min(currentData.length, start + CARDS_VISIBLE);
    return { cardStart: start, cardEnd: end };
  }, [currentData, isExpiry, upcomingRange.from]);

  const displayedCards = showAllCards ? currentData : currentData.slice(cardStart, cardEnd);
  // Сколько скрыто слева (прошлое) и справа (будущее)
  const hiddenBefore = cardStart;
  const hiddenAfter = currentData.length - cardEnd;
  const hiddenCount = hiddenBefore + hiddenAfter;

  return (
    <>
      {/* ── Заголовок + переключатель режима ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {isExpiry ? 'Динамика суммы по срокам годности' : 'Динамика суммы по дате записи'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            за все периоды · {currentData.length} мес.
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => { if (newMode) setMode(newMode); }}
          size="small"
          sx={{
            height: 32,
            '& .MuiToggleButton-root': {
              px: 1.5, py: 0.5,
              fontSize: '0.78rem', fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton value="recordDate">По дате записи</ToggleButton>
          <ToggleButton value="expiryDate">По срокам годности</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {currentData.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={6}>Нет данных</Typography>
      ) : (
        <>
          {/* ── График ── */}
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              key={mode}
              data={chartData}
              margin={{ top: 10, right: 24, left: 10, bottom: rotateLabels ? 16 : 8 }}
            >
              <defs>
                <linearGradient id={`areaGrad_${mode}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={mainColor} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={mainColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(m: string) => new Date(m + '-01').toLocaleString('ru', { month: 'short', year: '2-digit' })}
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                angle={xAxisAngle}
                textAnchor={xAxisAnchor as any}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                interval={tickInterval}
                height={xAxisHeight}
              />
              <YAxis
                tickFormatter={fmtSumShort}
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                width={56}
              />
              <RTooltip
                content={<DynamicsTooltip mainColor={mainColor} isExpiry={isExpiry} />}
                cursor={{ stroke: mainColor, strokeWidth: 1.5, strokeDasharray: '3 3' }}
                isAnimationActive={false}
              />
              <Area
                key={`area_${mode}`}
                type="monotone"
                dataKey="total"
                fill={`url(#areaGrad_${mode})`}
                stroke="transparent"
                legendType="none"
                isAnimationActive={true}
                animationDuration={1400}
                animationEasing="ease-in-out"
                animationBegin={0}
              />
              <Line
                key={`line_${mode}`}
                type="monotone"
                dataKey="total"
                name="Сумма"
                stroke={mainColor}
                strokeWidth={2.5}
                dot={{ r: chartData.length > 48 ? 2 : 4, fill: mainColor, strokeWidth: 2, stroke: 'white' }}
                activeDot={{ r: 7, fill: activeColor, stroke: 'white', strokeWidth: 2 }}
                isAnimationActive={true}
                animationDuration={1400}
                animationEasing="ease-in-out"
                animationBegin={0}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* ── Карточки по месяцам ── */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {displayedCards.map((d, i) => {
              const isLast = !isExpiry && (!showAllCards
                ? i === displayedCards.length - 1
                : i === currentData.length - 1);
              const upcoming = isExpiry && isUpcomingExpiry(d.month, upcomingRange.from, upcomingRange.to);
              const isPast = isExpiry && d.month < upcomingRange.from;

              // Приоритет цветов: истекает скоро > последний > прошедший
              const borderColor = upcoming ? '#ef4444'
                : isLast ? mainColor
                  : isPast ? '#cbd5e1'
                    : 'divider';

              const bgColor = upcoming ? 'rgba(239,68,68,0.07)'
                : isLast ? `${mainColor}10`
                  : 'transparent';

              const textColor = upcoming ? '#ef4444'
                : isLast ? mainColor
                  : isPast ? '#94a3b8'
                    : 'text.primary';

              return (
                <Box key={d.month} sx={{
                  px: 1.5, py: 0.75, borderRadius: 2, border: '1px solid',
                  borderColor,
                  bgcolor: bgColor,
                  minWidth: 96, textAlign: 'center',
                  transition: 'all 0.15s ease',
                  ...(upcoming && {
                    boxShadow: '0 0 0 2px rgba(239,68,68,0.2)',
                  }),
                }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ display: 'block', ...(isPast && { color: '#94a3b8' }) }}>
                    {new Date(d.month + '-01').toLocaleString('ru', { month: 'short', year: '2-digit' })}
                    {upcoming && (
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: '#ef4444' }}>●</Box>
                    )}
                  </Typography>
                  <Typography
                    variant="caption" fontWeight={800}
                    sx={{ display: 'block', color: textColor }}
                  >
                    {fmtSum(d.total)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ── Кнопка «Показать все» / «Скрыть» ── */}
          {hiddenCount > 0 && (
            <ButtonBase
              onClick={() => setShowAllCards(v => !v)}
              sx={{
                mt: 1, display: 'flex', alignItems: 'center', gap: 0.5,
                color: 'text.secondary', fontSize: '0.78rem', fontWeight: 600,
                px: 1.5, py: 0.5, borderRadius: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background 0.15s',
              }}
            >
              {showAllCards
                ? <><ExpandLess sx={{ fontSize: 16 }} />Скрыть ({hiddenCount} мес.)</>
                : <>
                    <ExpandMore sx={{ fontSize: 16 }} />
                    Показать все
                    {hiddenBefore > 0 && <> · {hiddenBefore} раньше</>}
                    {hiddenAfter > 0 && <> · {hiddenAfter} позже</>}
                  </>
              }
            </ButtonBase>
          )}
        </>
      )}
    </>
  );
};

export default MonthlyDynamicsChart;
